import {SpanIcon} from '@mazraeapp/react_icon_kit';
import {useFormikContext} from 'formik';
import {useEffect, useMemo} from 'react';

import Button from '../../../../../../../components/button';

import {ILoanRequestRuleResponse} from '../../../../../../../services/hookService/getLoanRequestRule/types';

import {getPersianOrdinal} from '../../../../../../../utilities/helper/unitConvertor/getPersianOrdinal';

import ImagePreviewBox from '../../../components/ImagePreviewBox';
import PersonDescription from '../../../components/PersonDescription';

import {IBankDocsGeneralForm} from '../../LoanDocsPage';

import {
  getGuarantorCheckCount,
  getSelfButtonTitle,
  getSelfCheckCount,
  PersonSectionConfigs,
} from '../consts/PersonSectionConfigs';

import {checkPersonComplete, PersonPrefix} from './MultiPersonValidation';

interface PersonPreviewCardProps {
  personPrefix: PersonPrefix;
  onEdit: (prefix: PersonPrefix) => void;
  loanRequestRules: ILoanRequestRuleResponse;
}

interface PersonFieldItemProps {
  icon?: string;
  title: string;
  value?: string | number;
  className?: string;
}

const PersonFieldItem = ({
  icon,
  title,
  value,
  className = 'grid grid-cols-[1.25fr,0.75fr] gap-4',
}: PersonFieldItemProps) => {
  if (!value && value !== 0) return null;

  return (
    <div className={className}>
      <div className="flex items-start gap-1 text-sm text-gray-700 min-w-[70px]">
        {icon && <i className={`text-lg ${icon}`} />}
        <span>{title}</span>
      </div>

      <span className="text-sm break-words">{value}</span>
    </div>
  );
};

const PersonPreviewCard = ({
  personPrefix,
  onEdit,
  loanRequestRules,
}: PersonPreviewCardProps) => {
  const {values, setFieldValue} = useFormikContext<IBankDocsGeneralForm>();

  const person = values[personPrefix];

  const config = PersonSectionConfigs[personPrefix];

  const selfCheckCount = getSelfCheckCount(loanRequestRules);

  const guarantorCheckCount = getGuarantorCheckCount(loanRequestRules);

  const inCompleteButtonTitle =
    personPrefix === 'self'
      ? getSelfButtonTitle(loanRequestRules)
      : config.buttonTitle;

  const CompleteButtonTitle =
    personPrefix === 'self'
      ? `${selfCheckCount} عدد چک به نام خود فرد`
      : ` ضامن ${getPersianOrdinal(guarantorCheckCount)}`;

  const isComplete = checkPersonComplete(values, personPrefix);

  // dynamic cheque configs
  const chequeConfigs = useMemo(() => {
    return [
      {
        enabled: loanRequestRules?.owner_first_cheque_price > 0,

        title:
          selfCheckCount > 1 && personPrefix === 'self'
            ? ` چک ضمانت ${getPersianOrdinal(1)}`
            : 'چک ضمانت',

        sayadiField: 'first_sayadi_cheque_id' as const,

        digitalField: 'first_digital_cheque_id' as const,

        imageField: 'first_check_image' as const,
      },

      {
        enabled: loanRequestRules?.owner_second_cheque_price > 0,

        title: ` چک ضمانت ${getPersianOrdinal(2)}`,

        sayadiField: 'second_sayadi_cheque_id' as const,

        digitalField: 'second_digital_cheque_id' as const,

        imageField: 'second_check_image' as const,
      },

      {
        enabled: loanRequestRules?.owner_third_cheque_price > 0,

        title: ` چک ضمانت ${getPersianOrdinal(3)}`,

        sayadiField: 'third_sayadi_cheque_id' as const,

        digitalField: 'third_digital_cheque_id' as const,

        imageField: 'third_check_image' as const,
      },
    ].filter(item => item.enabled);
  }, [loanRequestRules]);

  // dynamic fields
  const personFields = useMemo(() => {
    const fields: {
      title: string;
      value?: string | number;
    }[] = [];

    // guarantor fields
    if (person?.name) {
      fields.push({
        title: 'نام و نام خانوادگی ضامن',
        value: person?.name,
      });
    }

    if (person?.cellphone_number) {
      fields.push({
        title: 'شماره موبایل ضامن',
        value: person?.cellphone_number,
      });
    }

    // dynamic sayadi fields
    chequeConfigs.forEach(config => {
      const sayadiValue = person?.[config.sayadiField];

      if (sayadiValue) {
        fields.push({
          title: `شناسه صیاد ${config.title}`,
          value: sayadiValue,
        });
      }
    });

    // dynamic digital cheque fields
    chequeConfigs.forEach(config => {
      const digitalValue = person?.[config.digitalField];

      if (digitalValue) {
        fields.push({
          title: `شناسه صیاد ${config.title}`,
          value: digitalValue,
        });
      }
    });

    return fields;
  }, [person, chequeConfigs]);

  // dynamic image previews
  const imagePreviews = useMemo(() => {
    return chequeConfigs
      .map(config => {
        const uploadedImage = person?.[config.imageField];

        if (!uploadedImage) return null;

        let previewUrl: string | null = null;

        if (uploadedImage instanceof File) {
          previewUrl = URL.createObjectURL(uploadedImage);
        }

        if (typeof uploadedImage === 'string') {
          previewUrl = uploadedImage;
        }

        if (!previewUrl) return null;

        return {
          title: config.title,
          previewUrl,
          imageField: config.imageField,
          uploadedImage,
        };
      })
      .filter(Boolean);
  }, [chequeConfigs, person]);

  // cleanup object urls
  useEffect(() => {
    return () => {
      imagePreviews.forEach(item => {
        if (item?.uploadedImage instanceof File && item?.previewUrl) {
          URL.revokeObjectURL(item.previewUrl);
        }
      });
    };
  }, [imagePreviews]);

  return (
    <div className="mt-2">
      {isComplete ? (
        <div className="relative flex flex-col gap-3">
          <span className="text-sm font-medium">{CompleteButtonTitle}</span>

          <div className="flex flex-col px-[1rem] pt-[1rem] pb-[2rem] border rounded-lg bg-gray-50">
            <div className="flex flex-col gap-3">
              {/* fields */}
              {personFields.map((field, index) => (
                <PersonFieldItem
                  key={index}
                  title={field?.title}
                  value={field?.value}
                />
              ))}

              {/* sayadi registered */}
              {chequeConfigs.some(config => person?.[config.sayadiField]) && (
                <div className="flex gap-2">
                  <SpanIcon className="icon-nameCheckmark" />

                  <span className="text-sm text-gray-600">
                    چک در سامانه صیاد ثبت شده است
                  </span>
                </div>
              )}

              {/* images */}
              {imagePreviews.length > 0 && (
                <>
                  <span className="text-sm font-normal text-gray-700">
                    {imagePreviews.length > 1
                      ? 'تصاویر روی چک ها'
                      : 'تصویر روی چک'}
                  </span>
                  <div className="flex flex-wrap gap-3">
                    {imagePreviews.map((image, index) => {
                      if (!image) return null;

                      return (
                        <div key={index} className="flex flex-col gap-2">
                          <ImagePreviewBox
                            imageUrl={image.previewUrl}
                            showCheckTitle={false}
                            onRemove={() => {
                              setFieldValue(
                                `${personPrefix}.${image.imageField}`,
                                null,
                              );
                            }}
                            variant="sm"
                          />
                        </div>
                      );
                    })}
                  </div>
                </>
              )}

              {/* edit button */}
              <div className="absolute flex items-center gap-3 bottom-3 left-3">
                <i
                  onClick={() => onEdit(personPrefix)}
                  className="text-[20px] text-farm-shop-500 icon-nameEdit"
                />
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="p-[1rem] text-sm rounded-md text-farm-shop-500 bg-[#FAFDF7]">
          <PersonDescription
            personType={personPrefix}
            rules={loanRequestRules}
          />

          <Button
            variant="custom"
            customVariantClassName="bg-[#FAFDF7] text-farm-shop-500 text-md font-medium min-h-8 border-farm-shop-500 border-[1px] rounded-xl"
            className="w-full mt-3"
            onClick={() => onEdit?.(personPrefix)}
          >
            {inCompleteButtonTitle}
          </Button>
        </div>
      )}
    </div>
  );
};

export default PersonPreviewCard;
